import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AppHeader } from '../components/AppHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast } from '../components/Toast';
import { useEditor } from './EditorContext';
import { ItemListPanel } from './ItemListPanel';
import { ItemDetailPanel } from './ItemDetailPanel';
import { DetailsSheet } from './DetailsSheet';
import { SaveBlockedOverlay } from './SaveBlockedOverlay';
import { useUpdateAgenda, useGhostStatus } from '../api/queries';
import { useTenant } from '../auth/TenantProvider';
import { useMediaQuery } from '../util/useMediaQuery';
import { getAccentColor, type Agenda, type PublishTarget } from '../types/agenda';
import { api } from '../api/client';
import { AccountMenu } from '../auth/AccountMenu';
import './editor.css';

export function EditorView() {
  const navigate = useNavigate();
  const {
    draft,
    saveStatus,
    selectedIndex,
    select,
    resetTo,
    reloadFromServer,
    conflictBannerVisible,
    dismissConflictBanner,
    notification,
    clearNotification,
  } = useEditor();
  const update = useUpdateAgenda();
  const { activeTenant } = useTenant();
  const wide = useMediaQuery('(min-width: 860px)');

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(360);
  const [resizeDragging, setResizeDragging] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Surface context notifications as toasts.
  useEffect(() => {
    if (notification) {
      setToast(notification);
      clearNotification();
    }
  }, [notification, clearNotification]);

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    setResizeDragging(true);
    const onMove = (ev: PointerEvent) => {
      if (!bodyRef.current) return;
      const rect = bodyRef.current.getBoundingClientRect();
      setSidebarWidth(Math.max(200, Math.min(600, ev.clientX - rect.left)));
    };
    const onUp = () => {
      setResizeDragging(false);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  const accent = getAccentColor(draft.meetingType, activeTenant);

  const saveNow = async () => {
    try {
      const saved = await update.mutateAsync(draft);
      resetTo(saved);
      setToast('Saved');
    } catch (e) {
      setToast(`Save failed: ${(e as Error).message}`);
    }
  };

  const goBack = () => {
    if (saveStatus.type === 'saving' || saveStatus.type === 'offline') {
      setLeaveOpen(true);
    } else {
      navigate('/');
    }
  };

  const detailOpen = selectedIndex !== null;

  const saveLabel =
    saveStatus.type === 'saved'
      ? 'Saved'
      : saveStatus.type === 'saving'
        ? `Saving…`
        : saveStatus.type === 'offline'
          ? `Offline · ${saveStatus.pendingCount}`
          : saveStatus.type === 'blocked'
            ? 'Not saved ⚠'
            : '⚠';

  const header = (
    <AppHeader
      title={draft.title}
      accent={accent}
      onBack={goBack}
      actions={
        <>
          <span
            className={`save-status save-status--${saveStatus.type}`}
            aria-live="polite"
          >
            {saveLabel}
          </span>
          <EditorMenu
            agenda={draft}
            onSave={() => void saveNow()}
            onDetails={() => setDetailsOpen(true)}
            onToast={setToast}
          />
          <AccountMenu />
        </>
      }
    />
  );

  return (
    <div className="screen">
      {header}

      {conflictBannerVisible && (
        <div className="conflict-banner" role="alert">
          <span>Another editor changed the agenda structure.</span>
          <button className="btn btn-sm" onClick={() => void reloadFromServer()}>
            Reload
          </button>
          <button className="btn btn-ghost btn-sm" onClick={dismissConflictBanner}>
            Keep editing
          </button>
        </div>
      )}

      <div className={`editor-body${wide ? ' wide' : ''}`} ref={bodyRef}>
        <div className="editor-list-col" style={wide ? { width: sidebarWidth } : undefined}>
          <ItemListPanel />
        </div>

        {wide && (
          <div
            className={`editor-resize-handle${resizeDragging ? ' dragging' : ''}`}
            onPointerDown={startResize}
          />
        )}

        {wide ? (
          <div className="editor-detail-col">
            {detailOpen ? (
              <ItemDetailPanel index={selectedIndex!} />
            ) : (
              <div className="detail-empty">Select an item to edit</div>
            )}
          </div>
        ) : (
          <div className={`editor-detail-sheet${detailOpen ? ' open' : ''}`} aria-hidden={!detailOpen}>
            <div className="editor-detail-bar">
              <button className="btn btn-ghost" onClick={() => select(null)}>
                ‹ Items
              </button>
              <span className="editor-detail-bar-title">
                {selectedIndex !== null ? draft.items[selectedIndex]?.title : ''}
              </span>
            </div>
            <div className="editor-detail-sheet-body">
              {detailOpen && <ItemDetailPanel index={selectedIndex!} />}
            </div>
          </div>
        )}
      </div>

      <DetailsSheet open={detailsOpen} onClose={() => setDetailsOpen(false)} />

      <SaveBlockedOverlay />

      <ConfirmDialog
        open={leaveOpen}
        title="Changes are being saved"
        message={
          saveStatus.type === 'offline'
            ? `You have ${(saveStatus as { pendingCount: number }).pendingCount} unsaved change(s) that will be lost if you leave while offline.`
            : 'Your changes are currently being saved. Leave anyway?'
        }
        confirmLabel="Leave anyway"
        danger
        onConfirm={() => navigate('/')}
        onClose={() => setLeaveOpen(false)}
      />

      <Toast message={toast} onDone={() => setToast(null)} />
    </div>
  );
}

function EditorMenu({
  agenda,
  onSave,
  onDetails,
  onToast,
}: {
  agenda: Agenda;
  onSave: () => void;
  onDetails: () => void;
  onToast: (msg: string) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: ghostAvailable = false } = useGhostStatus();

  const publishMutation = useMutation({
    mutationFn: (targets: PublishTarget[]) =>
      api.publish(agenda.id, targets, agenda.spacingProfile),
    onSuccess: async (result) => {
      for (const [target, url] of Object.entries(result.outputs ?? {})) {
        if (target === 'ghost') {
          window.open(url, '_blank');
        } else if (target === 'markdown') {
          try {
            const text = await api.fetchOutputText(url);
            await navigator.clipboard.writeText(text);
            onToast('Markdown copied to clipboard');
          } catch {
            onToast('Failed to copy markdown');
          }
        } else {
          const base = `${agenda.date}-${agenda.meetingType}`;
          const filename =
            target === 'html' ? `${base}.html` :
            target === 'pdf' ? `${base}-letter.pdf` :
            target === 'pdf-13x19' ? `${base}-13x19.pdf` :
            url.split('/').pop() || 'download';
          const link = document.createElement('a');
          link.href = (await api.downloadOutput(url));
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      const errorEntries = Object.entries(result.errors ?? {});
      if (errorEntries.length > 0) {
        onToast(`Publish error: ${errorEntries[0][1]}`);
      } else if (!Object.keys(result.outputs ?? {}).length) {
        onToast('Published');
      }
    },
    onError: (err) => onToast(`Publish failed: ${(err as Error).message}`),
  });

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const action = (fn: () => void) => () => { setOpen(false); fn(); };
  const publish = (targets: PublishTarget[]) => action(() => publishMutation.mutate(targets))();

  return (
    <div className="editor-menu" ref={ref}>
      <button
        className="header-icon-btn"
        aria-label="Menu"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <HamburgerIcon />
      </button>
      {open && (
        <div className="editor-menu-dropdown" role="menu">
          <button className="editor-menu-item" role="menuitem" onClick={action(onSave)}>
            Save now
          </button>
          <button className="editor-menu-item" role="menuitem" onClick={action(onDetails)}>
            Agenda details
          </button>
          <div className="editor-menu-divider" />
          {agenda.meetingType === 'general' && (
            <button
              className="editor-menu-item"
              role="menuitem"
              onClick={action(() => navigate(`/agenda/${agenda.id}/timeline`))}
            >
              Timeline
            </button>
          )}
          <button
            className="editor-menu-item"
            role="menuitem"
            onClick={action(() => navigate(`/agenda/${agenda.id}/preview`))}
          >
            Preview
          </button>
          <div className="editor-menu-divider" />
          <button
            className="editor-menu-item"
            role="menuitem"
            onClick={() => publish(['html'])}
            disabled={publishMutation.isPending}
          >
            Download HTML
          </button>
          <button
            className="editor-menu-item"
            role="menuitem"
            onClick={() => publish(['pdf'])}
            disabled={publishMutation.isPending}
          >
            Download PDF
          </button>
          <button
            className="editor-menu-item"
            role="menuitem"
            onClick={() => publish(['pdf-13x19'])}
            disabled={publishMutation.isPending}
          >
            Download PDF 13×19
          </button>
          <button
            className="editor-menu-item"
            role="menuitem"
            onClick={() => publish(['markdown'])}
            disabled={publishMutation.isPending}
          >
            Copy Markdown to clipboard
          </button>
          {ghostAvailable && (
            <button
              className="editor-menu-item"
              role="menuitem"
              onClick={() => publish(['ghost'])}
              disabled={publishMutation.isPending}
            >
              Publish to Ghost
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
